/**
 * One palette for unit status, shared by every view that draws a unit.
 *
 * The map, grid and list each grew their own colour table and drifted into
 * contradicting each other — the grid painted Available BLUE and Occupied
 * dark slate while the map painted Available GREEN and Occupied blue, so the
 * same unit changed meaning depending on which tab you were looking at. The
 * list added a third reading (Reserved violet, Maintenance amber) that
 * clashed with the map's (Reserved amber, Maintenance slate).
 *
 * Colours live here so a status can only ever mean one thing.
 *
 *   TILE — map boxes and grid cells: translucent fill + saturated border.
 *   PILL — list badges: the same hues, flattened for small text.
 *
 * `Delinquent` and `Overdue` are the same condition (see
 * services/unit_status on the backend) and must always look identical.
 */

export const UNIT_STATUS_TILE = {
  Available: 'bg-emerald-200/70 border-emerald-400 text-emerald-900',
  Occupied: 'bg-blue-200/70 border-blue-400 text-blue-900',
  Reserved: 'bg-amber-200/70 border-amber-400 text-amber-900',
  Maintenance: 'bg-slate-200 border-slate-400 text-slate-700',
  Overdue: 'bg-red-200/70 border-red-500 text-red-900',
  Delinquent: 'bg-red-200/70 border-red-500 text-red-900',
};

// Hover tints for clickable tiles (the grid). Keyed the same so a caller can
// append without knowing which status it has.
export const UNIT_STATUS_TILE_HOVER = {
  Available: 'hover:bg-emerald-300/70',
  Occupied: 'hover:bg-blue-300/70',
  Reserved: 'hover:bg-amber-300/70',
  Maintenance: 'hover:bg-slate-300',
  Overdue: 'hover:bg-red-300/70',
  Delinquent: 'hover:bg-red-300/70',
};

export const UNIT_STATUS_PILL = {
  Available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Occupied: 'bg-blue-50 text-blue-700 border-blue-200',
  Reserved: 'bg-amber-50 text-amber-700 border-amber-200',
  Maintenance: 'bg-slate-50 text-slate-600 border-slate-200',
  Overdue: 'bg-red-50 text-red-700 border-red-200',
  Delinquent: 'bg-red-50 text-red-700 border-red-200',
};

export const UNIT_STATUS_FALLBACK_TILE =
  'bg-slate-200 border-slate-400 text-slate-700';
export const UNIT_STATUS_FALLBACK_PILL =
  'bg-slate-50 text-slate-600 border-slate-200';

// Legend rows, in the order an operator scans them. Shared so a view cannot
// show a swatch for a status it does not actually paint.
export const UNIT_STATUS_LEGEND = [
  { status: 'Available', label: 'Available' },
  { status: 'Occupied', label: 'Occupied' },
  { status: 'Reserved', label: 'Reserved' },
  { status: 'Overdue', label: 'Overdue' },
  { status: 'Maintenance', label: 'Maintenance' },
];

/** Just the `bg-*` class — for legend swatches. */
export const tileSwatch = (status) =>
  (UNIT_STATUS_TILE[status] || UNIT_STATUS_FALLBACK_TILE)
    .split(' ')
    .filter((c) => c.startsWith('bg-') || c.startsWith('border-'))
    .join(' ');
